import React, {useCallback, useEffect, useMemo, useState} from "react";
import {sendOperationUserAdmin} from "../../utils/Api";
import {Profile} from "../../utils/model/Profile";
import {
    Avatar,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorResponseDialog from "../station/components/ErrorResponseDialog";
import Button from "@mui/material/Button";
import OperationAdminDialog from "./OperationAdminDialog";
import {AvatarIcon} from "../../utils/assets/AvatarIcon";
import {useProfile} from "../../context/ProfileContext";
import {collection, getDocs, limit, orderBy, query, where} from "firebase/firestore";
import {firestore} from "../../config/firebase";
import ProfileConverter from "../../utils/converters/ProfileConverter";
import {UserType} from "../../utils/model/UserType";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate} from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';

const FLAGGED_TYPES = [UserType.SUSPECT, UserType.SPAMMER];
const FETCH_LIMIT = 500;

function normalize(s: string | undefined | null): string {
    return (s ?? "").trim().toLowerCase();
}

export default function AdminFlaggedUsers() {
    const {printDisplayName} = useProfile();
    const [users, setUsers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<TemplateStringsArray | undefined>();
    const [resultResponse, setResultResponse] = useState<string | undefined>(undefined);
    const navigate = useNavigate();
    const {profile} = useProfile();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [search, setSearch] = useState("");

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(query(
                collection(firestore, "profiles").withConverter(ProfileConverter),
                where("type", "in", FLAGGED_TYPES),
                orderBy("publicProfile.displayName", "asc"),
                limit(FETCH_LIMIT),
            ));
            setUsers(snapshot.docs.map(d => d.data()) as Profile[]);
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const filteredUsers = useMemo(() => {
        const needle = normalize(search);
        if (!needle) { return users; }
        return users.filter(u => {
            return normalize(u.publicProfile?.displayName).includes(needle)
                || normalize(u.tcsRef).includes(needle)
                || normalize(u.id).includes(needle)
                || normalize(u.banReason).includes(needle);
        });
    }, [users, search]);

    const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const onSendOperationAdmin = async (e: React.MouseEvent<HTMLElement>, operationId: number, userId: string) => {
        setLoading(true);
        try {
            const result = await sendOperationUserAdmin(userId, operationId);
            setResultResponse(result.data.message);
            await loadUsers();
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const actionLabel = (type: UserType): string => type === UserType.SPAMMER ? "Reset to PARTICIPANT" : "Unban";

    return (
        <>
            {resultResponse &&
                <OperationAdminDialog message={resultResponse} onClose={() => setResultResponse(undefined)}/>
            }
            {error &&
                <ErrorResponseDialog errorCode={error} onClose={() => {
                    setError(undefined);
                }}/>
            }
            <Stack direction='column' height={'100%'} sx={{padding: "1rem"}}>
                <TextField
                    size="small"
                    placeholder="Search (display name, TCS ref, id, ban reason)"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    sx={{maxWidth: 400, mb: 2}}
                    InputProps={{
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => { setSearch(""); setPage(0); }}
                                    aria-label="Clear search">
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ) : undefined
                    }}
                />

                {loading &&
                    <CircularProgress
                        color="secondary"
                        size={64}
                        disableShrink
                        thickness={4}
                        sx={{margin: "auto"}}
                    />
                }
                {!loading && users &&
                    <Stack direction='column' gap={1} marginBottom={1}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Avatar</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Id</TableCell>
                                        <TableCell align="right">TCS Ref</TableCell>
                                        <TableCell align="right">Display Name</TableCell>
                                        <TableCell align="right">Ban Reason</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedUsers.map((user: Profile) => {
                                        const isSpammer = user.type === UserType.SPAMMER;
                                        return <TableRow key={user.id} sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                        }}>
                                            <TableCell component="th" scope="row">
                                                <Avatar sizes="small" alt={printDisplayName(user.publicProfile)}
                                                        src={AvatarIcon.forAvatar(user.publicProfile?.avatar)}/>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.type}
                                                    color={isSpammer ? "warning" : "error"}
                                                    size="small"/>
                                            </TableCell>
                                            <TableCell align="right">{user.id}</TableCell>
                                            <TableCell align="right">{user.tcsRef}</TableCell>
                                            <TableCell align="right">{user.publicProfile.displayName}</TableCell>
                                            <TableCell align="right">{isSpammer ? '—' : (user.banReason ?? 'UNDEFINED')}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction={'row'} gap={1} justifyContent={"flex-end"}>
                                                    {profile?.type === UserType.ADMIN &&
                                                        <Button
                                                            variant={'outlined'}
                                                            color={isSpammer ? 'warning' : 'error'}
                                                            onClick={(e) => onSendOperationAdmin(e, 3, user.id)}>
                                                            {actionLabel(user.type)}
                                                        </Button>
                                                    }
                                                    <Button
                                                        sx={{
                                                            borderStyle: "solid",
                                                            borderWidth: "1px",
                                                            minWidth: "auto"
                                                        }}
                                                        color={'warning'}
                                                        onClick={() => navigate("/admin/user/" + user.id)}>
                                                        <SearchIcon/>
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {filteredUsers.length > rowsPerPage &&
                            <TablePagination
                                component="div"
                                count={filteredUsers.length}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[25, 50, 100, 200]}
                            />
                        }
                    </Stack>
                }
                <Stack sx={{
                    flexDirection: "row",
                    gap: 1
                }}>
                    <Button
                        variant="contained"
                        type="button"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIcon/>
                    </Button>
                </Stack>
            </Stack>
        </>
    )
}
